import React, { createContext, useContext, useState, useEffect } from 'react';
import { CategoryInsightsSettingsContext } from 'contexts/CategoryInsightsSettingsContext';
import { CategoryManagementContext } from 'contexts/CategoryManagementContext';
import { getVendorPareto } from 'utils/apiRequests';
import {
	TooltipColor,
	TooltipContainer,
	TooltipItem,
	TooltipItemName,
	TooltipItemTitle,
	TooltipItemValue,
} from './DashboardChart.styles';
import {
	chartheight,
	FONTSTYLE,
	CHART_COLORS,
} from 'CategoryManagement/Dashboard/ChartsList/chartUtils';
import { renderToString } from 'react-dom/server';
import { colors } from '@alixpartners/ui-components';
import {
	formatSpendWithDecimalAndSymbol,
	getFormattedPercentage,
} from 'utils/number';

const VendorPareto = createContext();

const getPlotLines = series => {
	const dataObj = series.findLast(item => item.y > 80);
	if (dataObj) {
		return [
			{
				color: colors.chartMediumGreen,
				width: 1,
				value: dataObj.x,
				dashStyle: 'Dash',
			},
		];
	}
	return null;
};

export const VendorParetoProvider = ({
	children,
	currencySymbol,
	hideDecimals,
}) => {
	const [isLoading, setIsLoading] = useState(true);
	const [data, setData] = useState([]);
	const [options, setOptions] = useState({
		chart: {
			type: 'spline',
			height: chartheight,
		},
		boost: { useGPUTranslations: true },
		title: {
			text: 'Dashed line shows a 80% point per each company',
			style: {
				color: colors.grey5,
				fontSize: '13px',
			},
		},
		exporting: { enabled: false, allowHTML: true },
		credits: { enabled: false },
		xAxis: {
			crosshair: {
				dashStyle: 'Dash',
			},
			title: {
				text: ``,
				style: { ...FONTSTYLE },
			},
			lineColor: colors.black,
			labels: {
				style: { ...FONTSTYLE },
			},
			gridLineWidth: 0,
			// plotLines: getPlotLines(newData),
		},
		yAxis: {
			title: {
				align: 'high',
				text: '',
				textAlign: 'right',
				rotation: 0,
				y: -30,
				offset: 15,
				style: { ...FONTSTYLE },
			},
			gridLineWidth: 0,
			labels: {
				formatter: function() {
					return `${this.value}%`;
				},
				style: { ...FONTSTYLE },
			},
			stackLabels: {
				enabled: true,
				align: 'center',
				formatter: function() {
					return this.total > 0
						? formatSpendWithDecimalAndSymbol(
								this.total,
								currencySymbol,
								hideDecimals,
								0
						  ).toUpperCase()
						: null;
				},
				style: { ...FONTSTYLE },
			},
			lineColor: colors.black,
			lineWidth: 1,
		},
		legend: { enabled: false },
		tooltip: {
			crosshairs: true,
			shared: true,
			useHTML: true,
			backgroundColor: colors.white,
			borderWidth: 0,
			borderRadius: 2,
			formatter: function() {
				return renderToString(
					<TooltipContainer>
						<TooltipItemTitle>{this.x} vendors</TooltipItemTitle>
						{this.points.map(item => (
							<TooltipItem key={`${item.point.series.name}-${item.point.y}`}>
								<span>
									<TooltipColor color={item.color} />
									<TooltipItemName>{item.point.series.name}</TooltipItemName>
								</span>
								<TooltipItemValue>
									{getFormattedPercentage(item.point.y)} ({formatSpendWithDecimalAndSymbol(
										item.point.spend,
										currencySymbol,
										hideDecimals,
										2
									).toUpperCase()})
								</TooltipItemValue>
							</TooltipItem>
						))}
					</TooltipContainer>
				);
			},
		},
		plotOptions: {
			spline: { lineWidth: 3 },
			series: {
				turboThreshold: 1000000,
				marker: {
					states: {
						hover: {
							lineWidthPlus: 0,
							radiusPlus: 0,
						},
					},
					enabled: false,
					symbol: 'circle',
					radius: 6,
					radiusPlus: 0,
				},
			},
		},
		// series: [],
		colors: [CHART_COLORS[0]],
	});

	const { currentSelectCategory, dateRangeData, isFetching } = useContext(
		CategoryManagementContext
	);
	const { loading: isSettingsFetching } = useContext(
		CategoryInsightsSettingsContext
	);
	const { categoryName } = currentSelectCategory;
	const { dateFrom, dateTo } = dateRangeData;

	useEffect(
		() => {
			if (!isSettingsFetching && !isFetching) {
				setIsLoading(true);
				if (
					categoryName !== '' ||
					categoryName !== null ||
					categoryName !== undefined
				) {
					const payload = {
						categoryName: categoryName,
						dateFrom: dateFrom,
						dateTo: dateTo,
					};
					getVendorPareto(payload)
						.then(response => {
							if (response.length > 0) {
								setData(response);
								const [xValue, , , key4, yValue] = Object.keys(response[0]);
								const seriesData = response.map(item => ({
									spend: item[key4],
									y: item[yValue],
									x: parseInt(item[xValue].replace(/\D/g, '')),
								}));
								const lineResult = getPlotLines(seriesData);
								const seriesResult = [
									{
										name: 'Data',
										data: seriesData,
									},
								];
								setOptions(prevState => ({
									...prevState,
									xAxis: {
										...prevState.xAxis,
										plotLines: lineResult,
									},
									series: seriesResult,
								}));
								setIsLoading(false);
							} else {
								setIsLoading(false);
							}
						})
						.catch(() => setIsLoading(false));
				} else {
					setIsLoading(false);
				}
			}
		}, // eslint-disable-next-line
		[dateRangeData, categoryName, isSettingsFetching, isFetching]
	);

	return (
		<VendorPareto.Provider value={{ isLoading, data, options }}>
			{children}
		</VendorPareto.Provider>
	);
};

export const useVendorParetoContext = () => {
	return useContext(VendorPareto);
};
